import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { StyleManager } from "../../Managers/StyleManager";
import { AnimationStyle } from "../Styles/AnimationStyle";
import { ComponentTypes } from "../../ComponentTypes";
import { ShapeElement } from "./ShapeElement";
import { Tweens, ITweenable, TweensAttributes } from "../../Effects/Tweens";
import { StageController } from "../../StageController";
import { TextElement } from "../Texts/TextElement";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";
import { CustomUtils } from "../../../Utils/CustomUtils";

/**
 * AnimationElement can play animations from spritesheets.
 * AnimationElement uses spritesheets generated from editor (Adobe Animate) with json definition.
 * This element doesnt work without style. Style for this class is AnimationStyle.
 * Example:
 *   <AnimationElement Class="AnimationElement" Style="AnimationStyle" X="0" Y="0" />
 * @class AnimationElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class AnimationElement extends PIXI.extras.AnimatedSprite implements IElement, ITweenable {
    // event for dispatch end of movie clip
    static AnimationComplete: string = "animationComplete";

    static DynamicTint: string = "000000";

    private _uid: string;
    private _cloned: boolean;
    private __destroyed: boolean;

    // style is mandatory in this element
    protected _style: AnimationStyle;
    // animation settings for movie clip
    protected _startFrame: number;
    protected _endFrame: number;
    protected _stop: boolean;
    protected _randomStartFrame: boolean;
    protected _reverse: boolean;
    protected _continue: boolean;

    public usedInPool: boolean;

    maskSprite: PIXI.Sprite;

    static _destroyOptions: PIXI.DestroyOptions = {
        children: true,
    };

    /**
     * Let's create animation with empty texture
     */
    constructor() {
        let textureArray: Array<PIXI.Texture> = [PIXI.Texture.EMPTY];

        super(textureArray, false);
        PIXI.ticker.shared.add(this.update, this, PIXI.UPDATE_PRIORITY.HIGH);

        // default values
        this._startFrame = 0;
        this._randomStartFrame = false;

        this._cloned = false;
        this.__destroyed = false;

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    public update(deltaTime: number): void {
        if (this.playing) {
            super.update(deltaTime);
        }
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method _create
     * @param node is Element from xml
     * @return void
     */
    private createByNode(node: Element): void {
        this.assertDestroyed("_create()");

        if (DEBUG) {
            if (
                node.hasAttribute(AnimationElementAttributes.DEBUG) &&
                node.getAttribute(AnimationElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (this.name == null) {
            this.name = node.nodeName;
        }

        /*
         * Style define animation. You can use one style on more than one elements.
         * Style for AnimationElement has to be instance of AnimationStyle.
         * @property Style
         * @type string
         */
        if (
            node.hasAttribute(AnimationElementAttributes.STYLE) ||
            node.hasAttribute(AnimationElementAttributes.STYLE2)
        ) {
            if (node.hasAttribute(AnimationElementAttributes.STYLE)) {
                this._style = <AnimationStyle>StyleManager.Styles[node.getAttribute(AnimationElementAttributes.STYLE)];
            } else if (node.hasAttribute(AnimationElementAttributes.STYLE2)) {
                this._style = <AnimationStyle>StyleManager.Styles[node.getAttribute(AnimationElementAttributes.STYLE2)];
            }

            if (this._style != null) {
                if (this._style.startFrame != null) {
                    this._startFrame = this._style.startFrame;
                } else {
                    this._startFrame = null;
                }

                if (this._style.endFrame != null) {
                    this._endFrame = this._style.endFrame;
                } else {
                    this._endFrame = null;
                }

                if (this._style.stop != null) {
                    this._stop = this._style.stop;
                }

                if (this._style.animation !== "") {
                    let texture: PIXI.Texture;
                    let textures: PIXI.Texture[] = [];
                    if (this._endFrame != null) {
                        for (let i = this._startFrame; i <= this._endFrame; i++) {
                            let val = this.zerosPad(i, 4);
                            texture = PIXI.utils.TextureCache[this._style.animation + val];
                            if (texture != null) {
                                if (i == this._startFrame) {
                                    this.texture = texture;
                                }

                                textures.push(texture);
                            } else {
                                if (DEBUG) {
                                    this.log(
                                        "WARNING: No texture " +
                                            this._style.animation +
                                            val +
                                            " in TextureCache of AnimationElement!",
                                    );
                                }
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log(
                                "WARNING: animation has no endFrame number. Use ImageElement with attribute Animation instead AnimationElement!",
                            );
                        }

                        //  todo Disallow animations without endFrame
                        let frame = this.zerosPad(this._startFrame, 4);
                        texture = PIXI.utils.TextureCache[this._style.animation + frame];
                        if (texture != null) {
                            this.texture = texture;
                            textures.push(texture);
                        }
                    }

                    this.textures = textures;
                    if (this.textures.length > 0) {
                        this.onComplete = this._movieClipComplete.bind(this);

                        this.loop = false;
                        this.animationSpeed = 1;
                        this.blendMode = PIXI.BLEND_MODES.NORMAL;
                        this.alpha = 1;
                        this._reverse = false;
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no frames in " + this._style.animation);
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR animation in style is not defined");
                    }
                }

                if (this._style.loop != null) {
                    this.loop = this._style.loop;
                }

                if (this._style.reverse != null) {
                    this._reverse = this._style.reverse;
                }

                if (this._style.speed != null) {
                    this.animationSpeed = this._style.speed * (this._style.reverse ? -1 : 1);
                }

                if (this._style.tint != null) {
                    this.tint = this._style.tint;
                }

                if (this._style.blendMode != null) {
                    this.blendMode = CustomUtils.getBlendMode(this._style.blendMode);
                }

                if (this._style.alpha != null) {
                    this.alpha = this._style.alpha;
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR missing style: " + node.getAttribute(AnimationElementAttributes.STYLE));
                }
            }
        }

        this._randomStartFrame = Attributes.assignBooleanAttribute(
            node,
            AnimationElementAttributes.RANDOM_START_FRAME,
            this._randomStartFrame,
        );

        if (node.hasAttribute(AnimationElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(AnimationElementAttributes.MASK)) {
                    let shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(AnimationElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(
                node.getAttribute(AnimationElementAttributes.BITMAP_MASK),
            );

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(AnimationElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(
                        node.getAttribute(AnimationElementAttributes.BITMAP_MASK_X),
                    );
                }
                if (node.hasAttribute(AnimationElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(
                        node.getAttribute(AnimationElementAttributes.BITMAP_MASK_Y),
                    );
                }
                if (!node.hasAttribute(AnimationElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(AnimationElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.SPEED)) {
            this.animationSpeed =
                parseFloat(node.getAttribute(AnimationElementAttributes.SPEED)) * (this._reverse ? -1 : 1);
        }

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(AnimationElementAttributes.DEBUG) &&
                node.getAttribute(AnimationElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (
            !node.hasAttribute("RemoveTweens") ||
            StringUtils.parseBoolean(node.getAttribute("RemoveTweens")) != false
        ) {
            Tweens.removeTweens(this);
        }

        this.visible = Attributes.assignBooleanAttribute(node, AnimationElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, AnimationElementAttributes.ALPHA, this.alpha);
        this.pivot.x = Attributes.assignIntAttribute(node, AnimationElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, AnimationElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(AnimationElementAttributes.ANCHOR)) {
            this.anchor.x = parseFloat(node.getAttribute(AnimationElementAttributes.ANCHOR));
            this.anchor.y = parseFloat(node.getAttribute(AnimationElementAttributes.ANCHOR));
        }

        if (node.hasAttribute(AnimationElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(AnimationElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(AnimationElementAttributes.START_ROTATION)) {
            if (
                node.getAttribute(AnimationElementAttributes.START_ROTATION) ===
                AnimationElementAttributes.START_ROTATION
            ) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.position.x = Attributes.assignIntAttribute(node, AnimationElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, AnimationElementAttributes.Y, this.position.y);

        if (node.hasAttribute(AnimationElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(AnimationElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(AnimationElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(AnimationElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(AnimationElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(AnimationElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(AnimationElementAttributes.SCALE));
        }

        if (node.hasAttribute(AnimationElementAttributes.SKEW_X)) {
            this.skew.x = parseFloat(node.getAttribute(AnimationElementAttributes.SKEW_X));
        }

        if (node.hasAttribute(AnimationElementAttributes.SKEW_Y)) {
            this.skew.y = parseFloat(node.getAttribute(AnimationElementAttributes.SKEW_Y));
        }

        if (
            node.hasAttribute(AnimationElementAttributes.CONTINUE) &&
            StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.CONTINUE))
        ) {
            this._continue = true;

            if (node.hasAttribute(AnimationElementAttributes.SPEED)) {
                this.animationSpeed =
                    parseFloat(node.getAttribute(AnimationElementAttributes.SPEED)) * (this._reverse ? -1 : 1);
            }

            this.playMe();
        } else {
            this._continue = false;
        }

        if (
            node.hasAttribute(AnimationElementAttributes.PLAY) &&
            StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.PLAY))
        ) {
            this._stop = false;
            this._reverse = false;
            if (
                node.hasAttribute(AnimationElementAttributes.REVERSE) &&
                StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.REVERSE))
            ) {
                this._reverse = StringUtils.parseBoolean(node.getAttribute(AnimationElementAttributes.REVERSE));
                this.animationSpeed = this.animationSpeed * (this._reverse ? -1 : 1);
            }

            if (node.hasAttribute(AnimationElementAttributes.SPEED)) {
                this.animationSpeed =
                    parseFloat(node.getAttribute(AnimationElementAttributes.SPEED)) * (this._reverse ? -1 : 1);
            }

            this.playMe();
        } /*else if (this._startFrame != null && this._endFrame == null) {
            this._stop = false;
            this.playMe();
        }*/

        /**
         * @deprecated
         */
        if (node.hasAttribute(AnimationElementAttributes.STOP)) {
            //this._stop = true;
            this.showMe(parseInt(node.getAttribute(AnimationElementAttributes.STOP)));
        }

        /**
         *
         */
        if (node.hasAttribute(AnimationElementAttributes.SHOW_FRAME)) {
            //this._stop = true;
            this.showMe(parseInt(node.getAttribute(AnimationElementAttributes.SHOW_FRAME)));
        }

        if (node.hasAttribute(AnimationElementAttributes.TINT)) {
            const tint = node.getAttribute(AnimationElementAttributes.TINT);

            switch (tint) {
                case "none":
                    this.tint = 0xffffff;
                    break;
                case "dynamic":
                    this.tint = parseInt(AnimationElement.DynamicTint, 16);
                    break;
                default:
                    this.tint = parseInt(node.getAttribute(AnimationElementAttributes.TINT), 16);
                    break;
            }
        }

        this.runPositionTween(node);
        this.runScaleTween(node);
        this.runRotationTween(node);
        this.runAlphaTween(node);
        this.runTween(node);

        if (DEBUG) {
            /*this._printDebugInfo({
             'element': 'Animation',
             'style': this._style.node.nodeName,
             'currentFrame': this.currentFrame,
             'currentTime': this._currentTime,
             'start': this._startFrame,
             'end': this._endFrame,
             'x': this.position.x,
             'y': this.position.y
             }, node);*/
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    /**
     * Play movie clip set up for this class.
     * If animation has Loop property set to true play will loop throught the movie clip.
     * If animation has Stop property set to true play will loop only single frame on start frame position.
     * If animation has Reverse property set to true play will start from frame on end frame position.
     *
     * @method play
     * @return void
     */
    playMe(frame: number = -1): void {
        if (!this._continue || !this.playing) {
            if (this._randomStartFrame && this._startFrame === 0 && this._endFrame != null) {
                this._startFrame = Math.floor(Math.random() * this._endFrame);
            }

            if (this._reverse) {
                this.gotoAndPlay(this._endFrame);
            } else {
                this.gotoAndPlay(this._startFrame);
            }
        }
    }

    /**
     *
     * @param {number} frameNumber
     */
    showMe(frameNumber: number): void {
        if (frameNumber != null) {
            this.gotoAndStop(frameNumber);
        } else {
            if (this._reverse) {
                this.gotoAndStop(this._endFrame);
            } else {
                this.gotoAndStop(this._startFrame);
            }
        }
    }

    /**
     * Return movie clip.
     * @method getMovieClip
     * @return PIXI.extras.AnimatedSprite
     *
     * @deprecated - use only "object" instead "object.getMovieClip()"
     */
    getMovieClip(): PIXI.extras.AnimatedSprite {
        return this;
    }

    /**
     * Dispatch event complete when movie clip goto last frame.
     * @method movieClipComplete
     * @return void
     */
    protected _movieClipComplete(): void {
        this.emit(AnimationElement.AnimationComplete);
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(AnimationElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        let alphaTween: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(AnimationElementAttributes.ALPHA_TWEEN),
        );

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            AnimationElementAttributes.ALPHA_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            AnimationElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, AnimationElementAttributes.ALPHA_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            AnimationElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition == AnimationElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_X)).map(
                (value) => value + this.position.x,
            );
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_Y)).map(
                (value) => value + this.position.y,
            );
        } else {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_Y));
        }

        let bezierX: Array<number> = null;
        let bezierY: Array<number> = null;
        let bezier: any = null;

        if (
            node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_BEZIER_X) &&
            node.hasAttribute(AnimationElementAttributes.POSITION_TWEEN_BEZIER_Y)
        ) {
            bezierX = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_BEZIER_X));
            bezierY = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.POSITION_TWEEN_BEZIER_Y));
            bezier = { values: [] };

            for (let i = 0; i < Math.min(bezierX.length, bezierY.length); i++) {
                bezier.values.push({ x: bezierX[i], y: bezierY[i] });
            }
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_DELAY,
            0,
        );
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            AnimationElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, bezier });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(AnimationElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        let rotationTween: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(AnimationElementAttributes.ROTATION_TWEEN),
        );

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            AnimationElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            AnimationElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(
            node,
            AnimationElementAttributes.ROTATION_TWEEN_DELAY,
            0,
        );
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            AnimationElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(AnimationElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(AnimationElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(AnimationElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(AnimationElementAttributes.SCALE_TWEEN)) {
            scaleTween = AnimationElement._getPointsFromPointsAttribute(
                node.getAttribute(AnimationElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        if (scaleTween && scaleTween.length) {
            tweenValues = scaleTween;
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: scaleTweenY[i],
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenX) {
            tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                return {
                    x: scaleXValue,
                    y: this.scale.y,
                } as PIXI.Point;
            });
        } else if (scaleTweenY) {
            tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                return {
                    x: this.scale.x,
                    y: scaleYValue,
                } as PIXI.Point;
            });
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            AnimationElementAttributes.SCALE_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            AnimationElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, AnimationElementAttributes.SCALE_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            AnimationElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     *
     * @param {Element} node
     * @returns {boolean}
     */
    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                if (tween === "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    /**
     * Formats values in case of characters "c/+/-".
     * @param tween
     * @param values
     */
    private formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    private static _getPointsFromPointsAttribute(pointsString: string): any {
        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) => {
                return /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair];
            })
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Add zero padding
     * @param {number} num
     * @param {number} size
     * @returns {string}
     */
    private zerosPad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return AnimationElement
     */
    clone(): AnimationElement {
        let element: AnimationElement = new AnimationElement();

        element._style = this._style;
        element._startFrame = this._startFrame;
        element._endFrame = this._endFrame;
        element._stop = this._stop;
        element._randomStartFrame = this._randomStartFrame;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.tint = this.tint;
        element.blendMode = this.blendMode;
        element.name = this.name;
        element.skew.x = this.skew.x;
        element.skew.y = this.skew.y;

        if (this.textures != null && this.textures.length > 0) {
            element.texture = this.texture;
            element.textures = this.textures;
            element.onComplete = element._movieClipComplete.bind(element);
            if (this._style != null && this._style.loop != null) {
                element.loop = this._style.loop;
            }

            element.animationSpeed = this.animationSpeed;

            //element.removeChildren();
            /* todo Potrebujeme ? */
        }

        element.playMe = this.playMe.bind(element);
        element.showMe = this.showMe.bind(element);
        element.stop = this.stop.bind(element);
        //element.getMovieClip = this.getMovieClip.bind(element);

        element.markAsCloned(); // must be called last before return!

        return element;
    }

    /**
     * Marks element as cloned
     */
    protected markAsCloned(): void {
        this._markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    _markAsCloned(): void {
        this.assertDestroyed("_markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);
                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    protected removeAndDestroyChildren(): void {
        this._removeAndDestroyChildren();
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    _removeAndDestroyChildren(): void {
        this.assertDestroyed("_removeAndDestroyChildren");

        let destroyOptions: PIXI.DestroyOptions = {
            children: true,
        };

        if (this.children != null && this.children.length > 0) {
            for (let i = this.children.length - 1; i >= 0; i--) {
                if (this.children[i] != null) {
                    (this.children[i] as any).destroy(destroyOptions);
                }
            }
        }

        this.removeChildren();
    }

    /**
     *
     * @param method
     */
    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     *
     * @param method
     * @private
     */
    /*    protected _assertDestroyed(method: string): void
     {
     if (DEBUG) {
     if (this.__destroyed) {
     throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
     }
     }
     }*/

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.assertDestroyed("destroy");

        if (Utils.isNullOrUndefined(options)) {
            options = AnimationElement._destroyOptions;
        }

        this.internalDestroy(options);

        if (!Utils.isNullOrUndefined(this.children)) {
            super.destroy();
        }

        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.destroy(this);
            }
        }

        this.__destroyed = true;
    }

    /**
     * @method internalDestroy - cleanup resources
     * @param options
     */
    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        this.onComplete = null;
        this.textures = [PIXI.Texture.EMPTY];

        Tweens.removeTweens(this);
    }

    /**
     *
     *
     * @returns {any}
     */
    getDebugInfo(): string {
        if (DEBUG) {
            let debugInfoFn = this.debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }

        return "";
    }

    /**
     *
     * @returns {any}
     */
    debugInfo(): string {
        if (DEBUG) {
            let result = `${this.uid}\nName: [${this.name}]`;
            let animation = this._style ? this._style.animation : "";
            if (!StringUtils.isNullOrEmpty(animation)) {
                result += "\nAnimation: " + animation;
            }

            return result;
        }

        return "";
    }

    /**
     * @property cloned - indicates if element was cloned or not.
     * @returns {boolean}
     */
    public get cloned(): boolean {
        return this._cloned;
    }

    public get destroyed(): boolean {
        return this.__destroyed;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    /**
     *
     * @param speed
     */
    public changeAnimationSpeed(speed: number): void {
        this.animationSpeed = speed;
    }

    /**
     * Prints output on screen inside element with specified debug content
     */
    protected _printDebugInfo(text: any, node?: Element) {
        if (DEBUG) {
            if (this.getChildByName(AnimationElementAttributes.DEBUG_INFO) != null) {
                //this.getChildByName(AnimationElementAttributes.DEBUG_INFO).destroy();
                this.removeChild(this.getChildByName(AnimationElementAttributes.DEBUG_INFO));
            }
            if (
                node == undefined ||
                (node.hasAttribute(AnimationElementAttributes.DEBUG_INFO) &&
                    node.getAttribute(AnimationElementAttributes.DEBUG_INFO) === "true")
            ) {
                // test
                /*let border = new ShapeElement();
                 border.beginFill(0xFFFFFF, 0);
                 border.lineStyle(2, 0xff0000);
                 border.drawRect(0, 0, this._textures[0].width, this._textures[0].height);
                 this.addChild(border);*/

                let textInfo = new TextElement();
                textInfo.name = AnimationElementAttributes.DEBUG_INFO;
                textInfo.x = textInfo.y = 0;
                textInfo.style.fill = 0xffffff;
                textInfo.style.fontSize = 12;
                textInfo.text = JSON.stringify(text, null, 4);

                this.addChild(textInfo);
            }
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}

export const AnimationElementAttributes = {
    /**
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    ANCHOR: "Anchor",
    /**
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    /**
     * If Continue is true animation is not restarted.
     * @property Continue
     * @type boolean
     */
    CONTINUE: "Continue",
    DEBUG_INFO: "DebugInfo",
    DEBUG: "Debug",
    /**
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    /**
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /**
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    /**
     * Start playing animation.
     * @property Play
     * @type boolean
     */
    PLAY: "Play",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_BEZIER_X: "PositionTweenBezierX",
    POSITION_TWEEN_BEZIER_Y: "PositionTweenBezierY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /**
     *
     */
    RANDOM_START_FRAME: "RandomStartFrame",
    /**
     * Indicates if animation should animate reverse.
     * @property Reverse
     * @type boolean
     * @default false
     */
    REVERSE: "Reverse",
    /**
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    /**
     * Tween rotation of this display object with children.
     * For duration use RotateTweenDuration (number - frames).
     * For tweenEase use RotateTweenEase (string - name of easeing function).
     * @property RotateTweenDegrees
     * @type Array<numbers> - consist of two values (start degrees; end degrees) or three values (start degrees, middle degrees, end degrees)
     * @default null
     */
    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /**
     * Tween scale of this display object with children.
     * For ease use ScaleTweenEase (string - name of easeing function).
     * For scaling X use ScaleTweenX (consist of two values (start scaleX; end scaleX) or three values (start scaleX; middle scaleX, end scaleX)).
     * For scaling Y use ScaleTweenY (consist of two values (start scaleY; end scaleY) or three values (start scaleY; middle scaleY, end scaleY)).
     * @property ScaleTweenDuration
     * @type number - frames
     * @default null
     */
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    /**
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    SCALE_X: "ScaleX",
    /**
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /**
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    /**
     * The factor to skew this display object horizontally.
     * @property SkewX
     * @type Number
     * @default 0
     */
    SKEW_X: "SkewX",
    /**
     * The factor to skew this display object vertically.
     * @property SkewY
     * @type Number
     * @default 0
     */
    SKEW_Y: "SkewY",
    /**
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    SHOW_FRAME: "ShowFrame",
    SPEED: "Speed",
    /**
     * @deprecated Instead this use ShowFrame attribute.
     * @property Stop
     * @type Number
     */
    STOP: "Stop",
    STYLE: "Style",
    STYLE2: "Style2",
    /**
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
    /**
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /**
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /**
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/AnimationElement.ts
 **/